import { useEffect } from "react";

/**
 * Custom hook to manage focus behavior in MUI DataGrid components.
 * 
 * This hook improves accessibility by:
 * - Making header cells non-focusable (WCAG 2.4.3 - Focus Order)
 * - Preventing cells with interactive elements from receiving focus when shift+tabbing
 * - Ensuring only interactive elements (links, buttons, etc.) are focusable
 * 
 * @param {Object} options - Configuration options
 * @param {Array} options.rows - Array of row data for the DataGrid
 * @param {Array} options.columns - Array of column definitions
 * @param {string} options.dataGridWidth - CSS class name for the DataGrid container (optional)
 * 
 * @example
 * useDataGridFocus({
 *   rows: gridRows,
 *   columns: gridColumns,
 *   dataGridWidth: 'my-grid-container'
 * });
 */
const useDataGridFocus = ({ rows, columns, dataGridWidth }) => {
  useEffect(() => {
    if (!rows || rows.length === 0) return;

    const setupTableFocus = () => {
      // Find the DataGrid container - try multiple selectors to work with all use cases
      let gridContainer = null;
      
      // Try to find by dataGridWidth class first
      if (dataGridWidth) {
        gridContainer = document.querySelector(`.${dataGridWidth} .MuiDataGrid-root`) || 
                       document.querySelector(`.${dataGridWidth}`);
      }
      
      // Fallback to any DataGrid root
      if (!gridContainer) {
        gridContainer = document.querySelector('.MuiDataGrid-root');
      }
      
      if (!gridContainer) return;
      
      return setupFocusForContainer(gridContainer);
    };

    const setupFocusForContainer = (gridContainer) => {
      // Make all header cells and header buttons non-focusable
      const headerCells = gridContainer.querySelectorAll(
        '.MuiDataGrid-columnHeader, ' +
        '.MuiDataGrid-columnHeader button, ' +
        '.MuiDataGrid-columnHeader [role="columnheader"], ' +
        '.MuiDataGrid-columnHeader [tabindex]'
      );

      headerCells.forEach((cell) => {
        cell.setAttribute('tabindex', '-1');
      });

      // Handle keyboard events to intercept shift+tab from links in cells
      const handleKeyDown = (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          const activeElement = document.activeElement;

          // Check if focus is on a link inside a DataGrid cell
          const link = activeElement.tagName === 'A' ? activeElement : activeElement.closest('a');
          if (link) {
            const cell = link.closest('.MuiDataGrid-cell');

            if (cell) {
              cell.setAttribute('tabindex', '-1');
            }
          }
        }
      };
      
      gridContainer.addEventListener('keydown', handleKeyDown, true);

      // Return cleanup function
      return () => {
        gridContainer.removeEventListener('keydown', handleKeyDown, true);
      };
    };

    // Wait for grid to render - use multiple timeouts to catch different render phases
    const timer1 = setTimeout(setupTableFocus, 100);
    const timer2 = setTimeout(setupTableFocus, 300);
    const timer3 = setTimeout(setupTableFocus, 500);

    let cleanupFn = null;
    const timer4 = setTimeout(() => {
      cleanupFn = setupTableFocus();
    }, 1000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      if (cleanupFn) cleanupFn();
    };
  }, [rows, columns, dataGridWidth]);
};

export default useDataGridFocus;

